/**
 * ESGBadge — Standalone iESG company card component
 * ─────────────────────────────────────────────────
 * Zero dependencies beyond React itself. Drop this file
 * into any React project and use it directly.
 *
 * Usage (static data):
 *   import ESGBadge from "./ESGBadge";
 *   <ESGBadge data={company} dashboardUrl="http://192.168.1.x:5173" />
 *
 * Usage (fetch from portal):
 *   <ESGBadge slug="natura" portalUrl="http://192.168.1.x:5173" />
 *
 * Usage (iframe embed in any HTML):
 *   <iframe src="http://192.168.1.x:5173/embed/natura"
 *           width="380" height="340" frameborder="0"
 *           style="border-radius:16px; border:none" />
 *
 * Data shape (when passing `data` prop):
 *   {
 *     name:       "Natura",
 *     slug:       "natura",
 *     sector:     "Cosméticos e Farmacêutica",
 *     rank_2025:  1,
 *     rank_2024:  8,          // null if first year
 *     iesg_2025:  731,        // 0–730 total score
 *     e_2025:     190,        // 0–250, optional
 *     s_2025:     156,        // 0–250, optional
 *     g_2025:     185,        // 0–250, optional
 *   }
 */

import { useState, useEffect } from "react";

// ─── helpers ──────────────────────────────────────────────────────────────────
function scoreColor(v) {
  if (v == null) return "#334155";
  const r = v / 730;
  if (r > 0.85) return "#10D874";
  if (r > 0.65) return "#4ADE80";
  if (r > 0.45) return "#FCD34D";
  if (r > 0.25) return "#FB923C";
  return "#F87171";
}

// ─── Pure SVG radar (E/S/G triangle) ─────────────────────────────────────────
function MiniRadar({ e, s, g, size = 110 }) {
  const max = 250, cx = size / 2, cy = size / 2, r = size * 0.38;
  const axes = [
    { label: "E", value: e ?? 0, angle: -90,  color: "#34D399" },
    { label: "S", value: s ?? 0, angle:  30,  color: "#60A5FA" },
    { label: "G", value: g ?? 0, angle: 150,  color: "#A78BFA" },
  ];
  const pt = (angle, radius) => {
    const rad = (angle * Math.PI) / 180;
    return [cx + radius * Math.cos(rad), cy + radius * Math.sin(rad)];
  };
  const poly = (level) =>
    axes.map((a, i) => { const [x, y] = pt(a.angle, r * level); return `${i ? "L" : "M"}${x},${y}`; }).join("") + "Z";
  const dataPoints = axes.map(a => pt(a.angle, r * Math.min(1, (a.value ?? 0) / max)));
  const dataPath = dataPoints.map(([x, y], i) => `${i ? "L" : "M"}${x},${y}`).join("") + "Z";

  return (
    <svg width={size} height={size} style={{ overflow: "visible", display: "block" }}>
      {[0.33, 0.66, 1].map(l => (
        <path key={l} d={poly(l)} fill="none" stroke="#1A3321" strokeWidth={1} />
      ))}
      {axes.map((a, i) => {
        const [x, y] = pt(a.angle, r);
        return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="#1A3321" strokeWidth={1} />;
      })}
      <path d={dataPath} fill="#10D87418" stroke="#10D874" strokeWidth={1.5} />
      {dataPoints.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={3} fill={axes[i].color} />
      ))}
      {axes.map((a, i) => {
        const [ax, ay] = pt(a.angle, r + 14);
        return (
          <text key={i} x={ax} y={ay} textAnchor="middle" dominantBaseline="middle"
            fill={a.color} fontSize={10} fontWeight="700" fontFamily="monospace">
            {a.label}
          </text>
        );
      })}
    </svg>
  );
}

// ─── Badge Card ───────────────────────────────────────────────────────────────
function BadgeCard({ data: company, dashboardUrl }) {
  if (!company) return (
    <div style={S.root}>
      <div style={{ padding: 32, color: "#475569", textAlign: "center" }}>
        Empresa não encontrada
      </div>
    </div>
  );

  const delta   = (company.rank_2024 != null && company.rank_2025 != null)
                  ? company.rank_2024 - company.rank_2025 : null;
  const isNew   = company.rank_2024 == null;
  const hasESG  = company.e_2025 != null;
  const color   = scoreColor(company.iesg_2025);
  const r = 38, dialCx = 44, circ = 2 * Math.PI * r;
  const offset  = circ * (1 - Math.min(1, (company.iesg_2025 ?? 0) / 730));
  const url     = dashboardUrl ?? "http://100.119.212.17:5173";

  const deltaLabel = delta == null ? null
    : delta > 0 ? `▲${delta}` : delta < 0 ? `▼${Math.abs(delta)}` : "↔";
  const deltaColor = delta > 0 ? "#10D874" : delta < 0 ? "#F87171" : "#475569";

  return (
    <div style={S.root}>
      {/* brand bar */}
      <div style={S.header}>
        <span style={S.brand}>iESG.</span>
        <span style={S.brandSub}>Integridade ESG 2025</span>
      </div>

      {/* company + dial */}
      <div style={S.companyRow}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={S.sector}>{company.sector}</div>
          <div style={S.name}>{company.name}</div>
          <div style={S.rankRow}>
            <span style={S.rank}>#{company.rank_2025}</span>
            {isNew
              ? <span style={S.newBadge}>★ estreante</span>
              : deltaLabel && <span style={{ ...S.delta, color: deltaColor }}>{deltaLabel}</span>}
          </div>
        </div>
        <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
          <svg width={88} height={88}>
            <circle cx={dialCx} cy={dialCx} r={r} fill="none" stroke="#1A3321" strokeWidth={6} />
            <circle cx={dialCx} cy={dialCx} r={r} fill="none" stroke={color} strokeWidth={6}
              strokeDasharray={circ} strokeDashoffset={offset}
              strokeLinecap="round" transform={`rotate(-90 ${dialCx} ${dialCx})`} />
            <text x={dialCx} y={dialCx + 1} textAnchor="middle" dominantBaseline="middle"
              fill={color} fontSize={15} fontFamily="monospace" fontWeight="700">
              {company.iesg_2025 == null ? "—" : Math.round(company.iesg_2025)}
            </text>
          </svg>
          <span style={S.dialLabel}>iESG score</span>
        </div>
      </div>

      {/* E/S/G */}
      <div style={S.esgRow}>
        {hasESG ? (
          <>
            <MiniRadar e={company.e_2025} s={company.s_2025} g={company.g_2025} size={110} />
            <div style={{ flex: 1 }}>
              {[
                { l: "E", v: company.e_2025, c: "#34D399", n: "Ambiental" },
                { l: "S", v: company.s_2025, c: "#60A5FA", n: "Social" },
                { l: "G", v: company.g_2025, c: "#A78BFA", n: "Governança" },
              ].map(ax => (
                <div key={ax.l} style={{ marginBottom: 9 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                    <span style={{ fontSize: 10, color: ax.c, fontWeight: 700, fontFamily: "monospace" }}>
                      {ax.l} · {ax.n}
                    </span>
                    <span style={{ fontSize: 10, color: "#64748B", fontFamily: "monospace" }}>
                      {ax.v == null ? "—" : Math.round(ax.v)}
                    </span>
                  </div>
                  <div style={{ height: 4, background: "#1A3321", borderRadius: 2 }}>
                    <div style={{ height: "100%", borderRadius: 2, background: ax.c,
                      width: `${Math.min(100, ((ax.v ?? 0) / 250) * 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <MiniRadar e={null} s={null} g={null} size={90} />
            <span style={{ fontSize: 11, color: "#334155" }}>
              Scores E·S·G detalhados disponíveis para empresas no Top 20 por eixo
            </span>
          </>
        )}
      </div>

      {/* CTA footer */}
      <div style={S.footer}>
        <span style={{ fontSize: 10, color: "#334155" }}>Powered by Integridade ESG</span>
        <a href={`${url}/#empresa/${company.slug}`} target="_blank" rel="noopener noreferrer"
          style={S.cta}>
          Ver análise completa →
        </a>
      </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = {
  root: {
    width: 360,
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    background: "#0D1F10",
    borderRadius: 16,
    border: "1px solid #1A3321",
    overflow: "hidden",
  },
  header: {
    background: "#061008",
    padding: "10px 18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: "1px solid #1A3321",
  },
  brand:    { fontSize: 13, fontWeight: 900, color: "#10D874", letterSpacing: "-0.02em" },
  brandSub: { fontSize: 10, color: "#334155", textTransform: "uppercase", letterSpacing: "0.08em" },
  companyRow: {
    padding: "18px 18px 14px",
    display: "flex",
    alignItems: "center",
    gap: 16,
    borderBottom: "1px solid #1A3321",
  },
  sector: { fontSize: 10, color: "#475569", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 },
  name: {
    fontSize: 18, fontWeight: 800, color: "#E2F0E6", letterSpacing: "-0.02em",
    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
  },
  rankRow: { display: "flex", alignItems: "center", gap: 8, marginTop: 5 },
  rank:     { fontSize: 15, fontWeight: 800, color: "#10D874", fontFamily: "monospace" },
  delta:    { fontSize: 11, fontWeight: 700 },
  newBadge: {
    fontSize: 10, color: "#FCD34D", background: "#FCD34D15",
    border: "1px solid #FCD34D40", borderRadius: 4, padding: "1px 6px",
  },
  dialLabel: { fontSize: 9, color: "#475569", textTransform: "uppercase", letterSpacing: "0.08em" },
  esgRow: {
    padding: "14px 18px",
    display: "flex",
    alignItems: "center",
    gap: 20,
    borderBottom: "1px solid #1A3321",
  },
  footer: {
    padding: "12px 18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  cta: {
    fontSize: 12, fontWeight: 700, color: "#10D874", textDecoration: "none",
    background: "#10D87415", border: "1px solid #10D87430",
    borderRadius: 7, padding: "6px 12px",
    display: "flex", alignItems: "center", gap: 5,
  },
};

// ─── ESGBadge — main export ───────────────────────────────────────────────────
/**
 * Props:
 *   data?        — company object (if you already have the data)
 *   slug?        — company slug to fetch from portal (requires portalUrl)
 *   portalUrl?   — base URL of the ESG portal, e.g. "http://192.168.1.10:5173"
 *   dashboardUrl?— override the "Ver análise completa" link URL
 */
export default function ESGBadge({ data, slug, portalUrl, dashboardUrl }) {
  const [fetched, setFetched] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    if (data || !slug || !portalUrl) return;
    setLoading(true);
    setError(null);
    fetch(`${portalUrl}/src/data/companies.json`)
      .then(r => { if (!r.ok) throw new Error("Fetch failed"); return r.json(); })
      .then(list => {
        const found = list.find(c => c.slug === slug);
        setFetched(found ?? null);
        if (!found) setError(`Empresa "${slug}" não encontrada`);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [slug, portalUrl, data]);

  if (loading) return (
    <div style={{ ...S.root, padding: 32, color: "#475569", textAlign: "center" }}>
      Carregando...
    </div>
  );
  if (error) return (
    <div style={{ ...S.root, padding: 32, color: "#F87171", textAlign: "center", fontSize: 12 }}>
      {error}
    </div>
  );

  const resolved = data ?? fetched;
  return <BadgeCard data={resolved} dashboardUrl={dashboardUrl ?? portalUrl} />;
}
